import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Generic in-app confirmation modal matching the app's dbModal* convention
 * (same look as the database delete/archive modals). Replaces window.confirm().
 *
 * Props:
 *   open        — when falsy, renders nothing
 *   title       — heading text
 *   name        — optional emphasised subject (e.g. the field name)
 *   body        — descriptive line
 *   variant     — "info" | "warn" | "danger" (drives badge/banner/button colour)
 *   icon        — short glyph shown in the badge
 *   confirmLabel— label for the confirm button
 *   onConfirm / onCancel — callbacks
 */
export default function ConfirmModal({
  open,
  title,
  name,
  body,
  variant = "info",
  icon,
  confirmLabel,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    // Only while open: a closed dialog that keeps listening would swallow Escape for the whole page.
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const v = ["info", "warn", "danger"].includes(variant) ? variant : "info";

  return (
    <div className="dbModalOverlay" onClick={onCancel}>
      <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onCancel}
          aria-label="Close"
          title="Close"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {icon != null && (
          <div className={`dbModalConfirmBadge dbModalConfirmBadge--${v}`}>{icon}</div>
        )}

        <div className="dbModalTitle">{title}</div>

        {name != null && <div className="dbModalDbName">{name}</div>}

        <div className={`dbModalConfirmBanner dbModalConfirmBanner--${v}`}>
          <p>{body}</p>
        </div>

        <div className="dbModalConfirmActions">
          <button type="button" className="dbModalCancel" onClick={onCancel}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            type="button"
            className={`dbModalConfirmBtn dbModalConfirmBtn--${v}`}
            onClick={onConfirm}
          >
            {confirmLabel || title}
          </button>
        </div>
      </div>
    </div>
  );
}
